import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
    DownloadLoads,
    PredictionLineChart,
    PredictionLoadRatesMap,
    PredictionLoadRatesBestOutbounds,
    RatePrediction,
    UserBuyRate,
} from "@/components/dataPages";
import { LockedLoadRates } from "./LockedLoadRates";
import { useQuery } from "@/utils/query";
import { clearLoadRatesState, getMarketToMarketRoute } from "@/state/loadRatesSlice";
import { ErrorPage } from "@/components/common/errors";
import { setSearchType } from "@/state/searchInputSlice";
import { useAreFeaturesUnlocked } from "@/auth/roles";
import { MarketOverview } from "@/components/home/MarketOverview";

export const PredictionLoadRates = () => {
    const [filteredMarkets, setFilteredMarkets] = useState(null);
    const { data: modelData, error } = useSelector((state) => state.loadRates.modelRatePrediction);
    const isLocked = !useAreFeaturesUnlocked();
    const { pickupZip, deliveryZip } = useQuery();
    // Load rates needs both ends of a route; without them there is nothing to predict.
    const hasRequiredParams = !!pickupZip && !!deliveryZip;
    const dispatch = useDispatch();

    useEffect(() => {
        if (hasRequiredParams) {
            dispatch(getMarketToMarketRoute({ pickupZip, deliveryZip }));
        }
    }, [pickupZip, deliveryZip, hasRequiredParams]);

    useEffect(() => {
        dispatch(setSearchType("load_rates"));
        return () => {
            dispatch(clearLoadRatesState());
        };
    }, []);

    if (isLocked) {
        return <LockedLoadRates />;
    }

    if (!hasRequiredParams) {
        return <MarketOverview />;
    }

    return !error ? (
        <div className="flex flex-col w-full w-max-[1436px] pt-1">
            <div className="flex flex-col bg-cm-p-surface rounded-lg w-full p-3 drop-shadow-[0_4px_4px_rgba(0,0,0,0.05)]">
                <div className="flex flex-col lg:flex-row w-full">
                    <div className="w-full lg:flex-1 min-w-0 overflow-hidden lg:mr-5">
                        <PredictionLineChart />
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-3 pt-4 lg:pt-0 border-t border-cm-p-outline-variation lg:border-0 lg:items-stretch lg:self-start lg:h-[345px] lg:w-[704px] lg:shrink-0">
                        <div className="w-full sm:flex-1 sm:min-w-0 min-h-[345px] bg-cm-p-background border border-cm-p-outline rounded-md p-3 flex">
                            <RatePrediction />
                        </div>
                        <div className="w-full sm:flex-1 sm:min-w-0 min-h-[345px] bg-cm-p-background border border-cm-p-accent-100 rounded-md p-3 flex">
                            <UserBuyRate />
                        </div>
                    </div>
                </div>
                <DownloadLoads filePath={modelData?.loadsCsvUrl} className="self-end h-10" />
            </div>
            <div className="overflow-hidden relative h-[1048px] md:h-[762px] lg:h-[397px] w-full flex mt-8 rounded-lg flex-col">
                <PredictionLoadRatesMap filteredMarkets={filteredMarkets} />
                <PredictionLoadRatesBestOutbounds setMarketsToPin={setFilteredMarkets} />
            </div>
        </div>
    ) : (
        <ErrorPage
            title="Oops! Something went wrong."
            description="Something went wrong while loading this page. If the problem persists, report it to our team using the feedback button on home page."
        />
    );
};
